import { Link } from "react-router-dom";
import {
  Scissors, Droplets, Wind, Sparkles, Shield, Clock,
  Armchair, ThermometerSun, Music, Coffee, ExternalLink
} from "lucide-react";

const VAN_URL = "https://customer-assets.emergentagent.com/job_barber-membership-1/artifacts/rvy2rpaa_20210411_153748.jpg";
const VAGARO_URL = "https://www.vagaro.com/dbhgg6";

const services = [
  { icon: Scissors, title: "Precision Haircut", desc: "Fades, tapers, lineups, classic cuts — tailored to your style and face shape." },
  { icon: Scissors, title: "Shape-Up / Edge-Up", desc: "Clean, sharp edges and hairline detailing to keep your look crisp between cuts." },
  { icon: Sparkles, title: "Beard Trim & Shape", desc: "Full beard grooming — trim, shape, line-up, and conditioning with premium oils." },
  { icon: Droplets, title: "Shampoo & Condition", desc: "Hot water shampoo station right in the van. Deep clean before every cut." },
  { icon: ThermometerSun, title: "Hot Towel Treatment", desc: "Classic barbershop hot towel finish for a clean, refreshing feel." },
  { icon: Sparkles, title: "Premium Styling", desc: "Finished with top-tier grooming products — pomades, oils, and styling creams." },
];

const vanFeatures = [
  { icon: Armchair, title: "Professional Barber Chair", desc: "Full-size hydraulic barber chair" },
  { icon: Droplets, title: "Hot & Cold Water", desc: "Built-in water system with shampoo station" },
  { icon: Wind, title: "Climate Controlled", desc: "A/C and heating for year-round comfort" },
  { icon: Shield, title: "Full Privacy", desc: "Enclosed van with tinted windows" },
  { icon: Coffee, title: "Refreshments", desc: "Complimentary beverages during your service" },
  { icon: Music, title: "Sound System", desc: "Premium audio — play your music" },
];

export default function ServicesPage() {
  return (
    <div data-testid="services-page" className="min-h-screen bg-[#0A0A0B]">
      {/* Hero */}
      <section className="relative pt-32 pb-24 md:pt-40 md:pb-32">
        <div className="px-6 md:px-12 lg:px-24">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-6">
              <div className="h-px w-12 bg-[#C5A059]" />
              <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#C5A059]">
                Our Services
              </span>
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-medium text-[#F3F4F6] leading-[1.1] mb-6">
              A full barbershop<br />
              <span className="text-[#C5A059]">at your door.</span>
            </h1>
            <p className="text-base md:text-lg text-[#A1A1AA] leading-relaxed font-light max-w-xl">
              Every service includes mobile setup at your location — home, office, or gym. 
              We bring everything. You just show up.
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-24 md:py-32 bg-[#0A0A0B]">
        <div className="px-6 md:px-12 lg:px-24">
          <div className="max-w-2xl mb-16">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-px w-12 bg-[#C5A059]" />
              <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#C5A059]">
                What We Offer
              </span>
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tight font-medium text-[#F3F4F6]">
              Services included with every membership
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((s, i) => (
              <div
                key={i}
                data-testid={`service-detail-${i}`}
                className="group p-8 rounded-md bg-[#141416] border border-white/5 hover:-translate-y-1 hover:border-white/15 transition-all duration-300"
              >
                <div className="w-12 h-12 rounded-sm bg-[#C5A059]/10 flex items-center justify-center mb-5 group-hover:bg-[#C5A059]/20 transition-colors duration-300">
                  <s.icon className="w-6 h-6 text-[#C5A059]" />
                </div>
                <h3 className="font-heading text-lg font-medium text-[#F3F4F6] mb-2">{s.title}</h3>
                <p className="text-sm text-[#A1A1AA] leading-relaxed font-light">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* The Mobile Studio */}
      <section className="py-24 md:py-32 bg-[#141416]">
        <div className="px-6 md:px-12 lg:px-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="h-px w-12 bg-[#C5A059]" />
                <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#C5A059]">
                  The Mobile Studio
                </span>
              </div>
              <h2 className="font-heading text-3xl sm:text-4xl tracking-tight font-medium text-[#F3F4F6] mb-6">
                Custom Mercedes Sprinter
              </h2>
              <p className="text-base text-[#A1A1AA] leading-relaxed font-light mb-8">
                Our custom-fitted high-roof Mercedes Sprinter van is a fully equipped barbershop 
                on wheels. Climate-controlled, sanitized between every appointment, and stocked with 
                professional-grade tools and premium products. The same quality you'd expect from 
                a top-tier shop — parked at your door.
              </p>
              <div className="grid grid-cols-2 gap-4">
                {vanFeatures.map((f, i) => (
                  <div key={i} data-testid={`van-feature-${i}`} className="flex items-start gap-3">
                    <f.icon className="w-5 h-5 text-[#C5A059] flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm text-[#F3F4F6] font-medium">{f.title}</p>
                      <p className="text-xs text-[#71717A]">{f.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-md overflow-hidden border border-white/5">
              <img
                src={VAN_URL}
                alt="LUXGROOM Mobile Barber Van"
                data-testid="services-van-image"
                className="w-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 md:py-32 bg-[#0A0A0B]">
        <div className="px-6 md:px-12 lg:px-24">
          <div className="max-w-2xl mb-16">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-px w-12 bg-[#C5A059]" />
              <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#C5A059]">
                How It Works
              </span>
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tight font-medium text-[#F3F4F6]">
              Three simple steps
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl">
            {[
              { step: "01", title: "Choose Your Plan", desc: "Pick the membership that matches your lifestyle — monthly, bi-weekly, or weekly." },
              { step: "02", title: "Schedule Your Cut", desc: "Book online through Vagaro or call us. Pick a time and location that works for you." },
              { step: "03", title: "We Come to You", desc: "Your dedicated barber arrives with the full mobile studio. Sit back. Get fresh." },
            ].map((s, i) => (
              <div key={i} data-testid={`step-${i}`} className="relative">
                <span className="font-heading text-6xl font-medium text-[#C5A059]/15 absolute -top-4 -left-2">{s.step}</span>
                <div className="relative pt-10">
                  <h3 className="font-heading text-xl font-medium text-[#F3F4F6] mb-2">{s.title}</h3>
                  <p className="text-sm text-[#A1A1AA] leading-relaxed font-light">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-[#0A0A0B]">
        <div className="px-6 md:px-12 lg:px-24 text-center max-w-2xl mx-auto">
          <h2 className="font-heading text-3xl sm:text-4xl tracking-tight font-medium text-[#F3F4F6] mb-4">
            Ready to get started?
          </h2>
          <p className="text-base text-[#A1A1AA] leading-relaxed font-light mb-8">
            Choose a membership plan or book a single appointment today.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/membership"
              data-testid="services-cta-membership"
              className="bg-[#C5A059] text-black hover:bg-[#D4AF37] font-medium transition-all duration-300 rounded-sm px-8 py-4 text-sm uppercase tracking-wider"
            >
              View Memberships
            </Link>
            <a
              href={VAGARO_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-transparent border border-white/20 text-white hover:border-[#C5A059] hover:bg-white/5 transition-all duration-300 rounded-sm px-8 py-4 text-sm uppercase tracking-wider flex items-center gap-2"
            >
              Book on Vagaro <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
